<script lang="ts">
  import Modal from '../common/Modal.svelte';
  import Button from '../common/Button.svelte';
  import { markWelcomeSeen } from '../../lib/onboardingState';

  type Props = {
    onStartWizard: () => void;
    onSkipTour: () => void;
  };

  let { onStartWizard, onSkipTour }: Props = $props();

  let isOpen = $state(true);

  function handleStartWizard() {
    markWelcomeSeen();
    isOpen = false;
    onStartWizard();
  }

  function handleSkipTour() {
    markWelcomeSeen();
    isOpen = false;
    onSkipTour();
  }
</script>

<Modal bind:isOpen={isOpen} size="md" showCloseButton={false}>
  <div class="welcome-container">
    <div class="welcome-header">
      <div class="logo-container">
        <svg
          class="logo-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          ></path>
        </svg>
      </div>
      <h1 class="welcome-title">Welcome to Keep Finance</h1>
      <p class="welcome-subtitle">Your privacy-first budget tracker</p>
    </div>

    <div class="welcome-content">
      <div class="feature-grid">
        <div class="feature-item">
          <div class="feature-icon" style="color: var(--color-primary);">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              ></path>
            </svg>
          </div>
          <h3 class="feature-title">Privacy First</h3>
          <p class="feature-description">
            All your financial data stays on your device. No cloud uploads, no tracking, complete
            control.
          </p>
        </div>

        <div class="feature-item">
          <div class="feature-icon" style="color: var(--color-success);">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              ></path>
            </svg>
          </div>
          <h3 class="feature-title">Smart Categorization</h3>
          <p class="feature-description">
            Automatically categorizes transactions using keywords, rules, and optional AI assistance.
          </p>
        </div>

        <div class="feature-item">
          <div class="feature-icon" style="color: var(--color-warning);">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              ></path>
            </svg>
          </div>
          <h3 class="feature-title">Powerful Insights</h3>
          <p class="feature-description">
            Track budgets, find recurring subscriptions, and visualize spending patterns with ease.
          </p>
        </div>

        <div class="feature-item">
          <div class="feature-icon" style="color: var(--color-purple);">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              ></path>
            </svg>
          </div>
          <h3 class="feature-title">Multi-Person Tracking</h3>
          <p class="feature-description">
            Perfect for families - track spending by person and split expenses easily.
          </p>
        </div>
      </div>

      <div class="getting-started">
        <h2 class="getting-started-title">Ready to get started?</h2>
        <p class="getting-started-text">
          We'll guide you through uploading your first transactions and exploring the key features.
        </p>
      </div>
    </div>

    <div class="welcome-actions">
      <Button variant="primary" size="lg" onclick={handleStartWizard}>
        Start Guided Setup
      </Button>
      <Button variant="secondary" size="lg" onclick={handleSkipTour}>
        Skip Tour - I'll Explore Myself
      </Button>
    </div>
  </div>
</Modal>

<style>
  .welcome-container {
    padding: var(--spacing-xl) var(--spacing-lg);
    max-width: 700px;
    margin: 0 auto;
  }

  .welcome-header {
    text-align: center;
    margin-bottom: var(--spacing-2xl);
  }

  .logo-container {
    display: flex;
    justify-content: center;
    margin-bottom: var(--spacing-md);
  }

  .logo-icon {
    width: 48px;
    height: 48px;
    color: var(--color-primary);
  }

  .welcome-title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-xs) 0;
  }

  .welcome-subtitle {
    font-size: var(--font-size-base);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .welcome-content {
    margin-bottom: var(--spacing-2xl);
  }

  .feature-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-lg);
    margin-bottom: var(--spacing-2xl);
  }

  .feature-item {
    text-align: center;
  }

  .feature-icon {
    width: 32px;
    height: 32px;
    margin: 0 auto var(--spacing-sm);
  }

  .feature-icon svg {
    width: 100%;
    height: 100%;
  }

  .feature-title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-xs) 0;
  }

  .feature-description {
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    margin: 0;
  }

  .getting-started {
    background-color: var(--color-background-secondary);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    text-align: center;
  }

  .getting-started-title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-xs) 0;
  }

  .getting-started-text {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    margin: 0;
  }

  .welcome-actions {
    display: flex;
    gap: var(--spacing-md);
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    .welcome-container {
      padding: var(--spacing-lg) var(--spacing-md);
    }

    .feature-grid {
      grid-template-columns: 1fr;
      gap: var(--spacing-md);
    }

    .welcome-actions {
      flex-direction: column;
    }

    .welcome-actions :global(button) {
      width: 100%;
    }
  }
</style>
